import { Box, Button, Dialog, DialogContent, Tooltip } from '@mui/material';
import { useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import deepCopy from 'lodash/cloneDeep';
import { fontSize, palette } from '../../..';
import ShowFormulaResult from './ShowFormulaResult';
import {
  resetCurrentCalculationSession,
  saveNewCalculation,
  updateCalculatedFieldName,
  updateCalculationName,
  updateDisplayFieldName,
  addTableIdToCurrentCalculationSession,
} from '../../../redux/Calculations/CalculationsActions';
import { TAlert } from '../../../types/types';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { messages } from '../../CommonFunctions/aliases';
import { isNameAllowed } from '../../CommonFunctions/CommonFunctions';
import ShortUniqueId from 'short-unique-id';
import LoadingPopover from '../../CommonFunctions/PopOverComponents/LoadingPopover';
import { fetchRecordsAndHeaders } from './helpers/fetchRecordsAndHeaders';
import { handleMultipleTablesCase } from './helpers/handleMultipleTablesCase';
import { getPreviousCalcs } from './helpers/getPreviousCalcs';
import { getSortedCalculationRequestBody } from './helpers/getSortedCalculationRequestBody';
import { isAggregationCalculation } from './helpers/isAggregationCalculation';
import { isDuplicateCalculationName } from './helpers/isDuplicateCalculationName';
import { handleAggregatedCalculation } from './helpers/handleAggregatedCalculation';
import { updateStateAfterSuccessfulSave } from './helpers/updateStateAfterSuccessfulSave';
import { removeCalculationId, setCalculationId } from '../../../redux/Parameters/ParametersAction';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
interface IFormulaResultProps {
  propKey: string;
  showPopup: boolean;
  orderedListOfFlows: string[];
  workspaceId: string;
  datasetId: string;
  extractedDs: boolean;
  dbConnectionId: string;
  canTestFormula?: boolean;
  tableRelationships:
    | {
        table1: string;
        table2: string;
        relationship: string;
        isDirect: boolean;
      }[]
    | null;
  onClose: () => void;
}
const FormulaDialog = ({
  extractedDs,
  propKey,
  showPopup,
  orderedListOfFlows,
  workspaceId,
  datasetId,
  dbConnectionId,
  tableRelationships,
  canTestFormula = true,
  onClose,
}: IFormulaResultProps) => {
  const { calculations, tablesInDatasets, chartProperties, chartGroup, parameters, chartControls } =
    useSelector((state: RootState) => ({
      calculations: state.calculations,
      tablesInDatasets: state.tabTileProps.tablesForSelectedDataSets,
      chartProperties: state.chartProperties,
      chartGroup: state.chartFilterGroup,
      parameters: state.parameters,
      chartControls: state.chartControls,
    }));
  const dispatch = useDispatch();

  const currentCalculationSession = useMemo(() => {
    return deepCopy(calculations.properties[propKey]?.currentCalculationSession);
  }, [propKey]);

  const [isInputActive, setIsInputActive] = useState<boolean>(false);
  const [alert, setAlert] = useState<TAlert | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const columnsInfo = useRef<string[]>([]);
  const isUppercase = ['oracle', 'snowflake'].includes(
    chartProperties.properties[propKey]?.selectedDs?.vendor,
  );
  const [calculationLocalName, setCalculationLocalName] = useState<string>(() => {
    return currentCalculationSession?.name ?? '';
  });
  const [loading, setLoading] = useState<boolean>(false);

  const handleCalculationSave = async () => {
    if (calculationLocalName.length > 40) {
      setAlert({ message: 'Calculation name cannot exceed 40 characters', severity: 'error' });
      return;
    }
    const isNameInvalid =
      calculationLocalName.includes('-') ||
      calculationLocalName.includes(' ') ||
      !isNameAllowed(calculationLocalName);

    if (isNameInvalid) {
      setAlert({ message: messages.formula.wrongName, severity: 'error' });
      return;
    }

    const fields = currentCalculationSession?.calculationInfo?.fields;
    if (!fields) {
      setAlert({ message: 'No fields found in calculation', severity: 'error' });
      return;
    }

    const tableList = Object.keys(fields).reduce((acc: any, key: string) => {
      if (!acc.includes(fields[key].tableId)) acc.push(fields[key].tableId);
      return acc;
    }, []);
    if (isUppercase) {
      currentCalculationSession.calculationInfo.calculatedFieldName =
        calculationLocalName.toUpperCase();
      dispatch(updateCalculatedFieldName(propKey, calculationLocalName.toUpperCase()));
    } else {
      currentCalculationSession.calculationInfo.calculatedFieldName =
        calculationLocalName.toLowerCase();
      dispatch(updateCalculatedFieldName(propKey, calculationLocalName.toLowerCase()));
    }

    if (
      calculations.properties[propKey]?.currentCalculationSession &&
      calculations.properties[propKey].currentCalculationSession.calculationInfo &&
      calculations.properties[propKey].currentCalculationSession.calculationInfo
        .displayFieldName === null
    ) {
      dispatch(
        updateDisplayFieldName(
          propKey,
          currentCalculationSession.calculationInfo.calculatedFieldName,
        ),
      );
      currentCalculationSession.calculationInfo.displayFieldName =
        currentCalculationSession.calculationInfo.calculatedFieldName;
    }

    let tableIdToPushInto: string | undefined;

    if (tableList.length === 1) {
      tableIdToPushInto = tableList[0];
      // dispatch(addTableIdToCurrentCalculationSession(tableIdToPushInto, propKey));
    } else {
      tableIdToPushInto = handleMultipleTablesCase(tableRelationships, setAlert);

      if (tableIdToPushInto === undefined) return;
      // dispatch(addTableIdToCurrentCalculationSession(tableIdToPushInto, propKey));
    }
    if (!tableIdToPushInto) {
      setAlert({ message: 'No table found to push calculation into', severity: 'error' });
      return;
    }
    dispatch(addTableIdToCurrentCalculationSession(tableIdToPushInto, propKey));
    const tables = tablesInDatasets[datasetId];
    if (!tables) {
      setAlert({ message: 'No tables found in dataset', severity: 'error' });
      return;
    }

    const tableInfo = tables.find((table: any) => table.id === tableIdToPushInto);
    if (!tableInfo) {
      setAlert({ message: 'Table not found', severity: 'error' });
      return;
    }

    const uuid = new ShortUniqueId({ length: 4 }).randomUUID();
    setLoading(true);

    const [nonAggCalcs, aggCalcs] = getPreviousCalcs(calculations, datasetId, tableIdToPushInto);

    const isCurrentCalcPresent = !!calculations.savedCalculations.find(
      (calcSession) => calcSession.uuid === currentCalculationSession.uuid,
    );

    if (
      !isCurrentCalcPresent &&
      isDuplicateCalculationName(
        calculations,
        calculationLocalName,
        currentCalculationSession,
        chartProperties.properties[propKey]?.selectedDs,
      )
    ) {
      if (alert) setAlert(null);
      setAlert({
        severity: 'warning',
        message: 'There is already a calculation with this name. Please choose a different name.',
      });
      setLoading(false);
      return;
    }

    /** after this point the  new calc name is unique in entire savedCalculation
     * so if calc is aggregated no need to check for unique name
     * but if its row level check its uniqueness in table
     */
    const calcInfo = currentCalculationSession.calculationInfo;

    const isAggregated = isAggregationCalculation(
      calcInfo,
      orderedListOfFlows[orderedListOfFlows.length - 1],
    );
    const lastFlowId = orderedListOfFlows[orderedListOfFlows.length - 1];
    const flowInfo = calcInfo.flows[lastFlowId];
    const dataType =
      flowInfo?.[0].flow === 'IfElse'
        ? calcInfo.selectedResultType[lastFlowId]
        : flowInfo?.[0].resultantDatatype;
    if (!dataType) {
      setAlert({
        severity: 'error',
        message: `Datatype not found for the last flow ${calcInfo.flows[lastFlowId]?.[0]?.flow ?? ''}. Please check the calculation.`,
      });
      setLoading(false);
      return;
    }

    const connparameters = calcInfo.flows[lastFlowId]?.[0]?.parameter;
    const calculatedFieldName = calcInfo.calculatedFieldName;

    if (isAggregated) {
      // check if the calculation is already present in the saved calculation list

      if (
        isCurrentCalcPresent &&
        isDuplicateCalculationName(
          calculations,
          calculatedFieldName!,
          currentCalculationSession,
          chartProperties.properties[propKey]?.selectedDs,
        )
      ) {
        setAlert({
          severity: 'error',
          message: `A column with the name "${calculatedFieldName}" already exists. Please choose a different name.`,
        });
        setLoading(false);
        return;
      }

      // setting calculation id for all parameters which is used in the calculation before saving the calculation session
      if (connparameters && connparameters.length > 0) {
        connparameters.forEach((parameter: any) => {
          dispatch(setCalculationId(currentCalculationSession.uuid ?? uuid, parameter.parameterId));
        });
      }

      // setting calculation id for all parameters which is used in the calculation before saving the calculation session
      for (const conditionFilterId in calcInfo.conditionFilters) {
        const conditionFilter = calcInfo.conditionFilters[conditionFilterId];
        conditionFilter[0].conditions.forEach((condition: any) => {
          const parameterIds = condition.parameterId;
          if (parameterIds.length <= 0) return;
          parameterIds.forEach((parameterId: string) => {
            dispatch(setCalculationId(currentCalculationSession.uuid ?? uuid, parameterId));
          });
        });
      }

      // if the calculation is already saved then do not need to perform the following steps to remove the deleted parameter from calculation
      if (isCurrentCalcPresent) {
        const calculationParameters = parameters.savedParameters.filter((param: any) =>
          param?.calculationId?.includes(currentCalculationSession.uuid),
        );
        if (currentCalculationSession.calculationInfo.flows[lastFlowId]?.[0]?.flow === 'IfElse') {
          for (const conditionFilterId in calcInfo.conditionFilters) {
            const conditionFilter = calcInfo.conditionFilters[conditionFilterId];
            conditionFilter[0].conditions.forEach((condition: any) => {
              const parameterIds = condition.parameterId;
              if (parameterIds.length <= 0) return;

              const removedParameters = calculationParameters.filter(
                (param: any) =>
                  param.parameterId !== parameterIds.find((p: any) => p === param.parameterId),
              );

              removedParameters.forEach((param: any) => {
                dispatch(removeCalculationId(currentCalculationSession.uuid!, param.parameterId));
              });
            });
          }
        } else {
          const removedParameters = calculationParameters.filter(
            (param: any) =>
              param?.parameterId !==
              connparameters.find((p: any) => p.parameterId === param.parameterId)?.parameterId,
          );
          removedParameters.forEach((param: any) => {
            dispatch(removeCalculationId(currentCalculationSession.uuid!, param.parameterId));
          });
        }
      }

      handleAggregatedCalculation(
        currentCalculationSession,
        calcInfo,
        chartProperties,
        tableInfo,
        dispatch,
        chartControls,
        dataType,
      );
      dispatch(saveNewCalculation(propKey, currentCalculationSession.uuid ?? uuid, true, dataType, orderedListOfFlows));
      dispatch(resetCurrentCalculationSession(propKey));
      setLoading(false);
      setIsInputActive(false); // Reset the isInputActive state when the calculation is saved
      onClose();
      return;
    } else {
      /**
       *
       * sortedCalcs does not include the current calculation session
       */
      const sortedCalcs = getSortedCalculationRequestBody(
        currentCalculationSession,
        calcInfo,
        nonAggCalcs,
        setAlert,
        setLoading,
      );

      if (!sortedCalcs) return;
      if (columnsInfo.current.length === 0) {
        const columnsDetails = await fetchRecordsAndHeaders(
          extractedDs,
          tableInfo,
          datasetId,
          workspaceId,
          dbConnectionId,
          sortedCalcs,
        );
        if (columnsDetails.isError) {
          setAlert({ severity: 'error', message: columnsDetails.message });
          setLoading(false);
          return;
        }
        columnsInfo.current = columnsDetails.data.map((col) => col.columnName);
      }

      // columnsInfo.current = columnsInfo.current.filter((col) => col !== calculatedFieldName);

      if (!currentCalculationSession.uuid && columnsInfo.current.includes(calculatedFieldName!)) {
        setAlert({
          severity: 'error',
          message: `A column with the name "${calculatedFieldName}" already exists. Please choose a different name.`,
        });
        setLoading(false);
        return;
      }
      const idx = calculations.savedCalculations.findIndex(
        (calc) => calc.uuid === currentCalculationSession.uuid,
      );
      let tableChange = false;
      let oldTableId = '';
      let oldCalcName;
      if (idx !== -1) {
        const oldName = calculations.savedCalculations[idx].name;
        oldTableId = calculations.savedCalculations[idx].tableId || '';
        oldCalcName = oldName;
        tableChange = oldTableId !== '' && oldTableId !== tableIdToPushInto;
        if (oldName !== calculatedFieldName && columnsInfo.current.includes(calculatedFieldName!)) {
          setAlert({
            severity: 'error',
            message: `A column with the name "${calculatedFieldName}" already exists. Please choose a different name.`,
          });
          setLoading(false);
          return;
        }
      }

      // check if the calculation is already present in the saved calculation list
      if (
        isCurrentCalcPresent &&
        isDuplicateCalculationName(
          calculations,
          calculatedFieldName!,
          currentCalculationSession,
          chartProperties.properties[propKey]?.selectedDs,
        )
      ) {
        setAlert({
          severity: 'error',
          message: `A column with the name "${calculatedFieldName}" already exists. Please choose a different name.`,
        });
        setLoading(false);
        return;
      }

      // setting calculation id for all parameters which is used in the calculation before saving the calculation session
      const connparameters = calcInfo.flows[lastFlowId]?.[0]?.parameter;
      if (connparameters && connparameters.length > 0) {
        connparameters.forEach((parameter: any) => {
          dispatch(setCalculationId(currentCalculationSession.uuid ?? uuid, parameter.parameterId));
        });
      }

      // setting calculation id for all parameters which is used in the calculation before saving the calculation session
      for (const conditionFilterId in calcInfo.conditionFilters) {
        const conditionFilter = calcInfo.conditionFilters[conditionFilterId];
        conditionFilter[0].conditions.forEach((condition: any) => {
          const parameterIds = condition.parameterId;
          if (parameterIds.length <= 0) return;
          parameterIds.forEach((parameterId: string) => {
            dispatch(setCalculationId(currentCalculationSession.uuid ?? uuid, parameterId));
          });
        });
      }

      // if the calculation is already saved then do not need to perform the following steps to remove the deleted parameter from calculation
      if (isCurrentCalcPresent) {
        const calculationParameters = parameters.savedParameters.filter((param: any) =>
          param?.calculationId?.includes(currentCalculationSession.uuid),
        );
        if (currentCalculationSession.calculationInfo.flows[lastFlowId]?.[0]?.flow === 'IfElse') {
          for (const conditionFilterId in calcInfo.conditionFilters) {
            const conditionFilter = calcInfo.conditionFilters[conditionFilterId];
            conditionFilter[0].conditions.forEach((condition: any) => {
              const parameterIds = condition.parameterId;
              if (parameterIds.length <= 0) return;

              const removedParameters = calculationParameters.filter(
                (param: any) =>
                  param.parameterId !== parameterIds.find((p: any) => p === param.parameterId),
              );

              removedParameters.forEach((param: any) => {
                dispatch(removeCalculationId(currentCalculationSession.uuid!, param.parameterId));
              });
            });
          }
        } else {
          const removedParameters = calculationParameters.filter(
            (param: any) =>
              param?.parameterId !==
              connparameters.find((p: any) => p.parameterId === param.parameterId)?.parameterId,
          );
          removedParameters.forEach((param: any) => {
            dispatch(removeCalculationId(currentCalculationSession.uuid!, param.parameterId));
          });
        }
      }

      updateStateAfterSuccessfulSave(
        tableInfo,
        datasetId,
        calcInfo,
        currentCalculationSession,
        chartProperties,
        chartGroup,
        propKey,
        dispatch,
        uuid,
        dataType,
        tableChange,
        oldTableId,
        chartControls,
        orderedListOfFlows,
        oldCalcName,
      );
      dispatch(resetCurrentCalculationSession(propKey));
      setLoading(false);
      setIsInputActive(false); // Reset the isInputActive state when the calculation is saved
      onClose();
    }
    if (isCurrentCalcPresent) {
      Object.entries(chartProperties.properties).forEach(([tabTileId, prop]: [string, any]) => {
        prop.chartAxes?.forEach((axis: any) => {
          axis.fields?.forEach((field: any) => {
            if (field.SavedCalculationUUID === currentCalculationSession.uuid) {
              dispatch(callQueryAPI(tabTileId, true));
            }
          });
        });
      });
    }
  };

  const isNameValid = (name: string) => {
    if (!name) return 'Calculation name cannot be empty.';
    if (name.length > 40) return 'Calculation name cannot exceed 40 characters.';
    if (name.includes('-') || name.includes(' ') || !isNameAllowed(name))
      return messages.formula.wrongName;
    return null;
  };

  return (
    <Dialog
      open={showPopup}
      onClose={(event: any, reason: any) => {
        event.stopPropagation();
        if (reason === 'escapeKeyDown') onClose();
      }}
      maxWidth="md"
      sx={{
        overflowY: 'scroll',
        maxHeight: '60vh',
        top: '20%',
      }}
    >
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      {loading ? <LoadingPopover /> : null}
      <DialogContent
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          position: 'relative',
          height: '100%',
          minHeight: '15rem',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Box>
          {isInputActive ? (
            <>
              <input
                name="calculation-name"
                className="calc-name-input"
                autoFocus
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  const value = isUppercase
                    ? e.target.value.toUpperCase()
                    : e.target.value.toLowerCase();

                  setCalculationLocalName(value);
                  setNameError(isNameValid(value));
                }}
                onBlur={(e) => {
                  if (nameError) {
                    e.target.focus();
                    return;
                  }
                  dispatch(updateCalculationName(propKey, calculationLocalName));
                  dispatch(updateDisplayFieldName(propKey, calculationLocalName));
                  setCalculationLocalName(calculationLocalName);
                  setIsInputActive(false);
                }}
                value={calculationLocalName}
                style={{
                  width: '100%',
                  height: '50px',
                  fontSize: '24px',
                  outline: 'none',
                  border: nameError ? '2px solid red' : '2px solid #2bb9bb',
                }}
                onFocus={(e) =>
                  (e.target.style.border = nameError ? '2px solid red' : '2px solid #2BB9BB')
                }
              />
              {nameError && (
                <div style={{ color: 'red', fontSize: '10px', marginTop: '4px' }}>{nameError}</div>
              )}
            </>
          ) : (
            <Tooltip
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -20],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
              placement="bottom"
              title="Double click to edit calculation name"
            >
              <div
                onDoubleClick={() => setIsInputActive(true)}
                style={{
                  width: '100%',
                  height: '50px',
                  padding: '8px',
                  paddingLeft: '0',
                  fontSize: '24px',
                  background: 'white',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {calculationLocalName}
              </div>
            </Tooltip>
          )}
        </Box>

        <div
          // onClick={() => {
          //   setIsInputActive(false);
          // }}
          style={{ overflowX: 'hidden', width: '30vw' }}
        >
          {canTestFormula ? (
            <ShowFormulaResult
              orderedListOfFlows={orderedListOfFlows}
              propKey={propKey}
              url={`test-calculated-field?${
                dbConnectionId ? `dbconnectionid=${dbConnectionId}&` : ''
              }workspaceId=${workspaceId}&datasetid=${datasetId}`}
              calculationName={calculationLocalName}
            />
          ) : (
            <div style={{ color: 'grey', fontSize: '16px', padding: '10px' }}>
              Result preview is not available as the calculation is dynamic.
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
            gap: '10px',
            marginTop: '10px',
          }}
        >
          <Button
            disabled={!!nameError}
            sx={{
              marginLeft: 'auto',
              color: 'white',
              border: 'none',
              padding: '3px 5px',
              fontSize: fontSize.medium,
              textTransform: 'none',
              backgroundColor: '#a7a7a7',
              '&.Mui-disabled': {
                opacity: 0.7,
                cursor: 'not-allowed',
              },
            }}
            variant="text"
            onClick={(e: any) => {
              e.stopPropagation();
              setIsInputActive(false);
              onClose();
            }}
          >
            Close
          </Button>
          <Button
            disabled={!!nameError}
            onClick={handleCalculationSave}
            sx={{
              marginLeft: 'auto',
              background: palette.primary.main,
              color: 'white',
              border: 'none',
              padding: '3px 5px',
              fontSize: fontSize.medium,
              textTransform: 'none',
              '&.Mui-disabled': {
                opacity: 0.7,
                cursor: 'not-allowed',
              },
            }}
          >
            Ok
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormulaDialog;
